// Site profile → Performance: the IQVIA DQS metric rollup for one golden site,
// averaged across every trial the site ran.
//
// Reading order is deliberate and matches how the numbers should be trusted:
//
//   1. Index metrics — site value ÷ that trial's median. Because the trial
//      median is divided out these are the only figures comparable across
//      trials, so they lead and they are the only ones coloured good/bad.
//   2. Performance metrics — the site's own raw figures. Averaged over trials
//      of very different sizes, so shown without a verdict.
//   3. Trial context — the medians and totals of the trials themselves. Not
//      site data at all, so it sits in its own block and says so.
//   4. Per-trial breakdown — the rows behind every average above.
//
// Every figure carries n (how many trials reported it) and its min–max range:
// most multi-trial sites swing widely between studies, and a bare mean hides it.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { useScopeParams } from "@/hooks/useScopeParams";
import { apiFetchJson } from "@/utils/apiFetch";
import type { TrialInfo } from "@/views/Portfolio/types";

import type { SiteMetricsDetail } from "../components/PerformanceMetricFormat";
import {
  PerformanceBenchmarkRow,
  PerformanceIndexTile,
  PerformanceMetricTile,
  PerformanceSection,
} from "../components/PerformanceMetricPanels";
import PerformanceTrialTable, {
  type TrialMetricRow,
} from "../components/PerformanceTrialTable";
import type { MasteredSite } from "../types";

async function fetchSiteMetrics(siteKey: string): Promise<SiteMetricsDetail> {
  return apiFetchJson<SiteMetricsDetail>(
    `${import.meta.env.VITE_API_URL}/data/portfolio/site-metrics-golden/${encodeURIComponent(siteKey)}/`,
  );
}

const INDEX_TOOLTIP =
  "Each metric is the site's value divided by the median site in the same trial, then averaged over the trials the site ran. 1.00× is exactly the trial median. Dividing out the median is what makes these figures comparable between trials of different sizes — unlike the raw values below. The count chip is how many trials reported the metric; the second chip is the lowest and highest value behind the average.";

const PERFORMANCE_TOOLTIP =
  "The site's own reported values, averaged across the trials that reported each one. These mix trials of very different sizes, so they are shown without a better/worse verdict — use the index metrics above for that. n is the number of contributing trials; the range shows the spread the average conceals.";

const BENCHMARK_TOOLTIP =
  "Trial-level context, not site performance. Each trial's median site and its overall totals are broadcast onto every site row, so these describe the studies this site took part in rather than the site itself. Shown here because the index metrics above are measured against them.";

const TRIALS_TOOLTIP =
  "One row per trial, with the values behind every average on this tab. A site averaging 2.25× may have run one trial at 5.00× and another at 0.50×, so the breakdown is where an outlier becomes visible.";

export default function PerformanceTab({
  site,
  trials,
}: {
  site: MasteredSite;
  trials: TrialInfo[];
}) {
  const { scope, id } = useScopeParams();

  const { data, isLoading } = useQuery<SiteMetricsDetail>({
    queryKey: ["site-metrics-golden", scope, id, site.site_key],
    queryFn: () => fetchSiteMetrics(site.site_key),
    enabled: !!scope && !!id && !!site.site_key,
    staleTime: 5 * 60 * 1000,
  });

  const trialNames = useMemo<Record<string, string>>(
    () =>
      Object.fromEntries(
        trials.map((trial): [string, string] => [
          trial.id,
          trial.name ?? trial.id,
        ]),
      ),
    [trials],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-48 text-[#8C8C8C] text-[13px]">
        Loading performance metrics…
      </div>
    );
  }

  if (!data || data.n_trials === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-48 text-[#8C8C8C]">
        <span className="text-[13px]">
          No performance metrics on record for this site.
        </span>
      </div>
    );
  }

  const trialRows = data.trials as TrialMetricRow[];

  return (
    <div className="flex flex-col gap-4">
      <PerformanceSection
        title={`Site vs trial median · ${data.n_trials} trials`}
        tooltip={INDEX_TOOLTIP}
      >
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
          {data.index_metrics.map((metric) => (
            <PerformanceIndexTile
              key={metric.key}
              metric={metric}
              trials={data.trials}
            />
          ))}
        </div>
      </PerformanceSection>

      <PerformanceSection
        title="Site performance"
        tooltip={PERFORMANCE_TOOLTIP}
      >
        <div className="grid grid-cols-2 gap-2 lg:grid-cols-3 xl:grid-cols-5">
          {data.performance.map((metric) => (
            <PerformanceMetricTile key={metric.key} metric={metric} />
          ))}
        </div>
      </PerformanceSection>

      <PerformanceSection
        title={`Trial context · medians across ${data.benchmarks.n_trials} trials`}
        tooltip={BENCHMARK_TOOLTIP}
      >
        <div className="grid grid-cols-1 gap-x-8 md:grid-cols-2 xl:grid-cols-3">
          {data.benchmarks.metrics.map((metric) => (
            <PerformanceBenchmarkRow key={metric.key} metric={metric} />
          ))}
        </div>
      </PerformanceSection>

      <PerformanceSection title="By trial" tooltip={TRIALS_TOOLTIP}>
        <PerformanceTrialTable
          rows={trialRows}
          indexMetrics={data.index_metrics}
          performanceMetrics={data.performance}
          trialNames={trialNames}
        />
      </PerformanceSection>
    </div>
  );
}
